import { useState } from "react";
import { Col } from "react-bootstrap";

import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../assets/layouts";
import {
  defaultSearchQuery,
  GRID_ACTION_TYPES,
  Module,
} from "../../assets/types";
import { Constants } from "../../assets/utils/lib/constants";
import Control from "../../assets/vfx/controls/control";
import { ControlDataGridColumnProps } from "../../assets/vfx/controls/components/datagrid/controlDataGrid";
import { SettingService } from "./settingService";
import { SearchDto, SearchOperator } from "../../assets/utils/lib/searchDto";
import { SearchContainer } from "../../assets/vfx/search/searchContainer";
import Form from "../../assets/vfx/form/form";
import {
  createActionColumn,
  createLayoutOptions,
} from "../../assets/configuration/gridConfig";
import { CommunityDto } from "../../services/dto";
import { SettingTypeDto } from "./settingContainer";

interface SettingPageProps<T> {
  controlStyles?: IControlStyles | undefined;
  communities?: CommunityDto[];
  moduleType: Module;
  onCustomEvent?: (actionType: string, dataId: string) => void;
}

const DATA_ID_FIELD = "id";
const COMMUNITY_MODULES = [Module.Admin, Module.PopHub, Module.Questionnaire];

const baseGridColumns = (): ControlDataGridColumnProps[] => [
  {
    data: DATA_ID_FIELD,
    title: "ID",
    visible: false,
    sortable: false,
    columnType: "number",
    alignment: "right",
  },
  {
    data: "name",
    title: "Name",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "18%",
  },
  {
    data: "description",
    title: "Description",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "34%",
  },
  {
    data: "value",
    title: "Value",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "24%",
  },
];

export const SettingPage = <T extends SettingTypeDto>(
  props: SettingPageProps<T>
) => {
  const [searchQuery, setSearchQuery] = useState<SearchDto>(defaultSearchQuery);
  const settingService: SettingService<T> = new SettingService<T>(
    props.moduleType
  );
  const settingGridQuery = settingService.gridQuery(searchQuery);
  const isGridLoading = settingGridQuery.isLoading || settingGridQuery.isFetching;
  const styles: IControlStyles = props.controlStyles ?? useControlStyles();

  const settings: T[] = settingGridQuery.data?.result ?? [];
  const hasData: boolean = settings.length > 0;
  const layoutOptions = createLayoutOptions(hasData);
  const actionColumn = createActionColumn(styles, DATA_ID_FIELD, [
    GRID_ACTION_TYPES.EDIT,
    GRID_ACTION_TYPES.VIEW,
  ]);

  const columns: ControlDataGridColumnProps[] = [...baseGridColumns()];

  const handleSearch = (values: { name: string }): void => {
    const filters = [];

    if (values.name && values.name.trim() !== Constants.emptyString) {
      filters.push({
        field: "name",
        operator: SearchOperator.Contains,
        value: values.name,
      });
    }

    if (filters.length > 0 && Array.isArray(defaultSearchQuery.filters)) {
      filters.push(...defaultSearchQuery.filters);
    }

    if (filters.length > 0) {
      setSearchQuery({
        ...defaultSearchQuery,
        filters,
      });
    }
  };

  const handleClear = (): void => {
    setSearchQuery((prev) => ({
      ...defaultSearchQuery,
      sorts: prev.sorts,
      pageSize: prev.pageSize ?? 10,
    }));
  };
  const handleNew = (): void =>
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  return (
    <PageContent title="Settings">
      <SearchContainer
        initialValues={{ name: Constants.emptyString }}
        enableReinitialize
        onSearch={handleSearch}
        onNew={handleNew}
        onClear={handleClear}
      >
        <Col lg={4}>
          <Form.Text name="name" label="Name" />
        </Col>
      </SearchContainer>

      <Control.DataGrid
        dataId={DATA_ID_FIELD}
        cols={columns}
        actionCol={actionColumn}
        data={settings}
        onCustomEvent={props.onCustomEvent}
        layoutOptions={layoutOptions}
        totalRecords={
          settingGridQuery.data?.totalSize ?? settings?.length ?? 0
        }
        query={searchQuery}
        loading={isGridLoading}
        onQueryChange={(patch) => {
          setSearchQuery((query) => ({
            ...query,
            ...patch,
            sorts: patch.sortBy ? [patch.sortBy] : query.sorts,
          }));
        }}
      />
    </PageContent>
  );
};
